import { GroupedItems } from "../dtos/responses/commonResponses.dto"

/** Removes falsy values from a list */
export const compact = <T>(array: (T | null | undefined | false | 0 | "")[]): T[] => {
  return array.filter(Boolean) as T[]
}

/** Wraps a single value in an array; `null`/`undefined` become an empty array. */
export const asArray = <T>(value: T | T[] | undefined | null): T[] => {
  if (value === undefined || value === null) {
    return []
  }
  return Array.isArray(value) ? value : [value]
}

/** Splits an array into chunks of specified size */
export const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

/**
 * Groups an array of items by a specified property or using a selector function
 * @param items Array of items to group
 * @param key Group key property
 * @returns Array of GroupedItems where each group contains items sharing the same group
 */
export function groupBy<T>(items: T[], key: keyof T): GroupedItems<T>[] {
  const groupMap = new Map<string, T[]>()

  // Group items by the selected key
  for (const item of items) {
    const groupName = String(item[key] ?? "")

    const group = groupMap.get(groupName) || []
    group.push(item)

    groupMap.set(groupName, group)
  }

  const result: GroupedItems<T>[] = []

  groupMap.forEach((items, groupName) => {
    result.push({
      groupName,
      items,
    })
  })

  return result
}

/**
 * Convenience function that groups items by their `groupName` property
 * @param items Array of items with groupName property
 * @returns Array of GroupedItems where each group contains items sharing the same group
 */
export function groupByName<T extends { groupName?: string }>(items: T[]): GroupedItems<T>[] {
  return groupBy(items, "groupName")
}
